export const SCHEDULE_WEEKDAYS = [
  { id: 1, label: "Пн" },
  { id: 2, label: "Вт" },
  { id: 3, label: "Ср" },
  { id: 4, label: "Чт" },
  { id: 5, label: "Пт" },
  { id: 6, label: "Сб" },
  { id: 0, label: "Вс" },
];

export function minutesToTime(totalMin) {
  const normalized = ((totalMin % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function parseTimeToMinutes(time) {
  const [h, m] = String(time || "0:0")
    .slice(0, 5)
    .split(":")
    .map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function addMinutesToTime(time, minutes) {
  return minutesToTime(parseTimeToMinutes(time) + Number(minutes || 0));
}

export function durationBetween(start, end) {
  const diff = parseTimeToMinutes(end) - parseTimeToMinutes(start);
  return diff > 0 ? diff : 60;
}

export function normalizeTimeInput(value) {
  const v = String(value || "").trim().slice(0, 5);
  if (/^\d{1,2}:\d{2}$/.test(v)) {
    const [h, m] = v.split(":").map(Number);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }
  }
  return "16:00";
}

export function normalizeDurationMin(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return 60;
  return Math.min(600, Math.round(n));
}

export function scheduleEntryId(weekday, startTime) {
  return `${weekday}-${startTime}`;
}

export function slotsToEntries(slots) {
  return (slots || []).map((sl) => {
    const start = String(sl.start_time || "16:00").slice(0, 5);
    const end = String(sl.end_time || "").slice(0, 5);
    const duration_min = end
      ? durationBetween(start, end)
      : Number(sl.duration_min) || 60;
    return {
      id: scheduleEntryId(sl.weekday, start),
      weekday: Number(sl.weekday),
      start_time: start,
      duration_min,
    };
  });
}

export function weekdayLabel(weekdayId) {
  return SCHEDULE_WEEKDAYS.find((d) => d.id === weekdayId)?.label || "?";
}

export function sortWeekdayIds(ids) {
  return [...ids].sort((a, b) => {
    const dayA = a === 0 ? 7 : a;
    const dayB = b === 0 ? 7 : b;
    return dayA - dayB;
  });
}

export function formatScheduleEntry(entry) {
  const day = weekdayLabel(entry.weekday);
  const end = addMinutesToTime(entry.start_time, entry.duration_min);
  return `${day}, ${entry.start_time}–${end} (${entry.duration_min} мин)`;
}

export function splitScheduleEntry(entry) {
  return {
    day: weekdayLabel(entry.weekday),
    start: entry.start_time,
    end: addMinutesToTime(entry.start_time, entry.duration_min),
    duration: entry.duration_min,
  };
}

export function formatDraftPreview(draftDays, startTime, durationMin) {
  if (!draftDays.length) return "";
  const days = sortWeekdayIds(draftDays).map(weekdayLabel).join(", ");
  const end = addMinutesToTime(startTime, durationMin);
  return `${days} · ${startTime}–${end} · ${durationMin} мин`;
}

export function sortScheduleEntries(entries) {
  return [...entries].sort((a, b) => {
    const dayA = a.weekday === 0 ? 7 : a.weekday;
    const dayB = b.weekday === 0 ? 7 : b.weekday;
    if (dayA !== dayB) return dayA - dayB;
    return parseTimeToMinutes(a.start_time) - parseTimeToMinutes(b.start_time);
  });
}

export function emptyScheduleDraft() {
  return {
    entries: [],
    draftDays: [],
    start_time: "16:00",
    duration_min: 60,
  };
}

/** Зал группы для отображения в онбординге */
export function groupBranchMeta(group, branches = []) {
  const id = group?.branch_id || group?.branch?.id || "";
  const name =
    group?.branch?.name ||
    branches.find((b) => b.id === id)?.name ||
    null;
  return { id, name: name || "Зал не указан" };
}

/** Сигнатура расписания для сравнения между группами */
export function scheduleEntriesSignature(entries) {
  return sortScheduleEntries(entries || [])
    .map(
      (e) =>
        `${e.weekday}:${String(e.start_time).slice(0, 5)}:${e.duration_min}`,
    )
    .join("|");
}

/**
 * Группы с одинаковым расписанием, но разными залами.
 * @returns {{ groups: { groupName: string, branchId: string, branchName: string }[], label: string }[]}
 */
export function findCrossVenueScheduleConflicts(
  groups,
  schedulesByGroupId,
  branches = [],
) {
  const buckets = new Map();

  for (const g of groups) {
    const entries = schedulesByGroupId[g.id]?.entries || [];
    if (!entries.length) continue;
    const sig = scheduleEntriesSignature(entries);
    const { id: branchId, name: branchName } = groupBranchMeta(g, branches);
    if (!buckets.has(sig)) buckets.set(sig, []);
    buckets.get(sig).push({
      groupName: g.name,
      branchId: branchId || branchName,
      branchName,
    });
  }

  const conflicts = [];
  for (const items of buckets.values()) {
    if (items.length < 2) continue;
    const branchKeys = new Set(items.map((i) => i.branchId));
    if (branchKeys.size < 2) continue;
    conflicts.push({
      groups: items,
      label: items
        .map((i) => `«${i.groupName}» — ${i.branchName}`)
        .join(", "),
    });
  }
  return conflicts;
}

export function crossVenueScheduleConfirmText(conflicts) {
  if (!conflicts.length) return "";
  const detail = conflicts
    .map((c) => `• ${c.label}`)
    .join("\n");
  return (
    "У нескольких групп в разных залах указано одно и то же время занятий:\n\n" +
    `${detail}\n\n` +
    "Тренировки не могут проходить в двух залах одновременно. Вы уверены, что хотите сохранить такое расписание?"
  );
}
